import { useState } from 'react'
import { X, Trash2, Check } from 'lucide-react'
import { nanoid } from 'nanoid'
import { useAppStore } from '../store'
import { Modal, useModalClose } from './ui/Modal'

const PRESET_COLORS = [
  '#A58BFF', // violet
  '#7FB0FF', // blue
  '#72DCC6', // teal
  '#7FE0B0', // mint
  '#F2CC6B', // amber
  '#F0956A', // orange
  '#F49CC8', // pink
  '#FF8FA3', // rose
  '#C4B3FF', // lavender
  '#D8D7E4', // silver
]

interface TagEditorProps {
  tagId: string | null
  onClose: () => void
  onDelete?: () => void
}

export function TagEditor(props: TagEditorProps) {
  return (
    <Modal
      onClose={props.onClose}
      label={props.tagId ? 'Edit tag' : 'New tag'}
      maxWidth={460}
    >
      <TagBody {...props} />
    </Modal>
  )
}

function TagBody({ tagId, onDelete }: TagEditorProps) {
  const close = useModalClose()
  const vault = useAppStore((s) => s.unlockedVault)
  const updateVaultData = useAppStore((s) => s.updateVaultData)

  const existingTag = tagId
    ? vault?.data.tags.find((t) => t.id === tagId)
    : null

  const [name, setName] = useState(existingTag?.name || '')
  const [color, setColor] = useState(
    existingTag?.color ||
      PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)],
  )

  if (!vault) return null

  const isEditing = !!existingTag

  const handleSave = () => {
    if (!name.trim()) return
    if (isEditing && existingTag) {
      updateVaultData((data) => ({
        ...data,
        tags: data.tags.map((t) =>
          t.id === existingTag.id ? { ...t, name: name.trim(), color } : t,
        ),
      }))
    } else {
      updateVaultData((data) => ({
        ...data,
        tags: [...data.tags, { id: nanoid(), name: name.trim(), color }],
      }))
    }
    close()
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSave()
      }}
    >
      <div className="mb-5 flex items-center gap-3">
        <h2 className="flex-1 font-display text-xl font-semibold tracking-tight">
          {isEditing ? 'Edit tag' : 'New tag'}
        </h2>
        <button
          type="button"
          className="icon-btn"
          aria-label="Close"
          onClick={close}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-5">
        <div>
          <label className="label" htmlFor="tag-name">
            Name
          </label>
          <input
            id="tag-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            placeholder="e.g. longread"
            autoFocus
          />
        </div>

        <fieldset>
          <legend className="label">Colour</legend>
          <div className="flex flex-wrap gap-2">
            {PRESET_COLORS.map((preset) => {
              const on = color.toLowerCase() === preset.toLowerCase()
              return (
                <button
                  key={preset}
                  type="button"
                  aria-label={`Colour ${preset}`}
                  aria-pressed={on}
                  onClick={() => setColor(preset)}
                  className={`grid h-10 w-10 place-items-center rounded-xl transition-transform duration-300 ease-spring ${
                    on
                      ? 'scale-110 ring-2 ring-white/70 ring-offset-2 ring-offset-[#16151F]'
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: preset }}
                >
                  {on && <Check className="h-4 w-4 text-[#140D33]" />}
                </button>
              )
            })}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <label className="text-sm text-faint" htmlFor="tag-color">
              Custom
            </label>
            <input
              id="tag-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-10 cursor-pointer rounded-xl border border-white/10 bg-transparent"
            />
            <input
              type="text"
              aria-label="Hex colour"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="input !min-h-[40px] w-32 font-mono text-sm uppercase"
              placeholder="#000000"
            />
          </div>
        </fieldset>

        <div>
          <span className="label">Preview</span>
          <span className="chip !min-h-[34px]">
            <span className="dot" style={{ background: color }} />
            {name || 'tag name'}
          </span>
        </div>
      </div>

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {isEditing && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="btn-danger sm:mr-auto"
          >
            <Trash2 className="h-4 w-4" />
            Delete tag
          </button>
        )}
        <button type="button" onClick={close} className="btn-secondary">
          Cancel
        </button>
        <button
          type="submit"
          disabled={!name.trim()}
          className="btn-primary sm:min-w-[110px]"
        >
          {isEditing ? 'Save' : 'Create tag'}
        </button>
      </div>
    </form>
  )
}
